import React, { useState, useLayoutEffect, useEffect, useRef } from 'react';
import {
  removeLiveView,
  collapseIframe,
  expandIframe,
} from '../../../helpers/helpers';
import { Card } from '@smarterservices/smarter-ui';
import { IconButton, IconArrowOpenUpLine, IconArrowOpenDownLine, CloseButton } from '@smarterservices/smarter-ui';
import { ParamsNotFoundError } from '../../../helpers/definedErrors';

const useWindowHeight = () => {
  const [windowHeight, setWindowHeight] = useState(0);

  useLayoutEffect(() => {
    // Function to update the height state
    const updateHeight = () => {
      setWindowHeight(window.innerHeight);
    };

    // Set initial height
    updateHeight();

    // Add event listener for window resize
    window.addEventListener('resize', updateHeight);

    // Clean up event listener on unmount
    return () => window.removeEventListener('resize', updateHeight);
  }, []);

  return windowHeight;
};

const LiveView = () => {
  const [webcamLabel, setWebcamLabel] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [webcamError, setWebcamError] = useState(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const windowHeight = useWindowHeight();

  // Clean up video streams on unmount
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  // Handle webcam stream when expanded or webcamLabel changes
  useEffect(() => {
    if (!isExpanded || !webcamLabel) return;

    let isMounted = true;
    let stream = null;

    const startWebcam = async () => {
      try {
        // First get all video devices
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices.filter(device => device.kind === 'videoinput');

        // Find the device with matching label
        const targetDevice = videoDevices.find(device => device.label === webcamLabel);

        if (!targetDevice) {
          throw new Error(`No webcam found with label containing: ${webcamLabel}`);
        }

        // Stop any existing stream
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
        }

        // Start new stream with the matching device
        stream = await navigator.mediaDevices.getUserMedia({
          video: {
            deviceId: {
              exact: targetDevice.deviceId
            },
            width: { ideal: 640 },
            height: { ideal: 480 }
          }
        });

        if (isMounted) {
          streamRef.current = stream;
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
          }
          setWebcamError(null);
        }
      } catch (err) {
        console.error('Error accessing webcam:', err);
        if (isMounted) {
          setWebcamError(`Failed to access webcam: ${err.message}`);
        }
      }
    };

    startWebcam();

    return () => {
      isMounted = false;
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [isExpanded, webcamLabel]);

  useEffect(() => {
    const initLiveView = async () => {
      const url = new URL(window.location.href);
      const liveViewParams = new Proxy(new URLSearchParams(url.search), {
        get: (searchParams, prop) => searchParams.get(prop),
      });
      if (
        !liveViewParams ||
        !liveViewParams.webcamLabel
      ) {
        throw new ParamsNotFoundError();
      }
      setWebcamLabel(liveViewParams.webcamLabel);

      const isExpanded = liveViewParams.isExpanded === 'true';
      setIsExpanded(isExpanded);

      if (!isExpanded) {
        collapseIframe('spLiveViewIframeId');
      }
    };
    initLiveView();
  }, []);

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
    if (isExpanded) {
      collapseIframe('spLiveViewIframeId');
    } else {
      expandIframe('spLiveViewIframeId');
    }
  };

  window.addEventListener('message', (event) => {
    if (event.data.type === 'sp_iframe_expand_state_change') {
      const isExpanded = event.data.isExpanded;
      setIsExpanded(isExpanded);
    }
  });

  const HEADER_HEIGHT = 50;
  const bodyHeight =
    windowHeight > 0 ? `${windowHeight - HEADER_HEIGHT}px` : '100vh';

  return webcamLabel ? (
    <Card background="secondary" borderColor="brand" borderWidth="large" borderRadius="x-small">
      <Card.Header background="brand">
        <Card.Title>Live View</Card.Title>
        <Card.Actions>
          {isExpanded ? (
            <IconButton
              withBackground={false}
              withBorder={false}
              margin="small"
              size="small"
              color="primary-inverse"
              screenReaderLabel="Collapse"
              focusColor="inverse"
              onClick={toggleExpand}
            >
              <IconArrowOpenUpLine />
            </IconButton>
          ) : (
            <IconButton
              withBackground={false}
              withBorder={false}
              margin="small"
              size="small"
              color="primary-inverse"
              screenReaderLabel="Expand"
              focusColor="inverse"
              onClick={toggleExpand}
            >
              <IconArrowOpenDownLine />
            </IconButton>
          )}
          <CloseButton
            color="primary-inverse"
            screenReaderLabel="Close"
            onClick={removeLiveView}
          />
        </Card.Actions>
      </Card.Header>
      {isExpanded && (
        <Card.Body padding="0" height={bodyHeight}>
          <div style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#E8EAEC'
          }}>
            {webcamError ? (
              <div style={{ color: 'white', textAlign: 'center', padding: '1rem' }}>
                {webcamError}
              </div>
            ) : (
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain'
                }}
              />
            )}
          </div>
        </Card.Body>
      )}
    </Card>
  ) : null;
};

export default LiveView;
